import CodeIcon from "@mui/icons-material/Code";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatClearIcon from "@mui/icons-material/FormatClear";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import ImageIcon from "@mui/icons-material/Image";
import LinkIcon from "@mui/icons-material/Link";
import RedoIcon from "@mui/icons-material/Redo";
import TableChartIcon from "@mui/icons-material/TableChart";
import UndoIcon from "@mui/icons-material/Undo";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Divider from "@mui/material/Divider";
import { Editor } from "@tiptap/react";
import { useState } from "react";
import { CirclePicker } from "react-color";
import { ChevronDown } from "src/icons/chevron-down";
import { ChevronUp } from "src/icons/chevron-up";
import { MagicWandIcon } from "src/icons/magic-wand-icon";

type Level = 1 | 2 | 3 | 4 | 5 | 6;

interface Props {
  editor: Editor;
  handleAidropdown: () => void;
  evaluationData: any;
  isEEATMode: boolean;
}

const MenuBar = ({
  editor,
  handleAidropdown,
  evaluationData,
  isEEATMode,
}: Props) => {
  const [openHeadingsDialog, setOpenHeadingsDialog] = useState(false);
  const [openTextColorDialog, setOpenTextColorDialog] = useState(false);
  const [openAlignDialog, setOpenAlignDialog] = useState(false);
  const [openLinkDialog, setOpenLinkDialog] = useState(false);
  const [link, setLink] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [selectedColor, setSelectedColor] = useState("#000000");
  const [isExplicitParagraph, setIsExplicitParagraph] = useState(false);
  const [showSelectionTooltip, setShowSelectionTooltip] = useState(false);
  const [tableAnchorEl, setTableAnchorEl] = useState<null | HTMLElement>(null);
  const [tableDimensions, setTableDimensions] = useState({ rows: 0, cols: 0 });
  const [hoveredDimensions, setHoveredDimensions] = useState({
    rows: 0,
    cols: 0,
  });
  const maxRows = 8;
  const maxCols = 8;

  if (!editor) {
    return null;
  }

  const handleTableButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setTableAnchorEl(event.currentTarget);
  };

  const handleTablePopoverClose = () => {
    setTableAnchorEl(null);
    setHoveredDimensions({ rows: 0, cols: 0 });
  };

  const handleCellHover = (row: number, col: number) => {
    setHoveredDimensions({ rows: row + 1, cols: col + 1 });
  };

  const handleTableInsert = () => {
    if (hoveredDimensions.rows > 0 && hoveredDimensions.cols > 0) {
      editor
        .chain()
        .focus()
        .insertTable({
          rows: hoveredDimensions.rows,
          cols: hoveredDimensions.cols,
          withHeaderRow: true,
        })
        .run();
    }
    handleTablePopoverClose();
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "96%",
        mt: "0.05rem",
        zIndex: 99999,
        backgroundColor: "white",
      }}
    >
      <ButtonGroup
        variant="outlined"
        aria-label="text formatting options"
        size="small"
        sx={{
          "& .MuiButtonGroup-grouped": {
            backgroundColor: "white !important",
            border: "none !important",
            borderRadius: "0px !important",
            boxShadow: "none !important",
            padding: "4px 10px !important",
          },

          // width: "100%",
        }}
      >
        {/* Text Formatting */}
        <Box>
          <Button
            onClick={() => editor.chain().focus().undo().run()}
            sx={{
              color: "#333333",
            }}
          >
            <UndoIcon />
          </Button>
          <Button
            onClick={() => editor.chain().focus().redo().run()}
            sx={{
              color: "#333333",
            }}
          >
            <RedoIcon />
          </Button>
        </Box>
        {/* /divider*/}
        <Box
          sx={{
            backgroundColor: "white",
          }}
        >
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              borderColor: "gray !important",

              height: "30px !important",
              margin: "0px 4px !important",
            }}
          />
        </Box>
        <Box>
          <Button
            onClick={() => setOpenHeadingsDialog(!openHeadingsDialog)}
            sx={{
              color: openHeadingsDialog ? "blue" : "#333333",
              position: "relative",
            }}
          >
            {/* Show 'Aa' only when the current node is a paragraph without any heading */}
            {editor.isActive("paragraph") && !editor.isActive("heading") && (
              <Typography
                sx={{
                  fontSize: "18px",
                  fontWeight: "700",
                  marginRight: "4px",
                  color: "#333333",
                }}
              >
                A{" "}
                <span style={{ marginLeft: "-4px", fontSize: "12px" }}>a</span>
              </Typography>
            )}

            {/* {editor.isActive("paragraph") && isExplicitParagraph && (
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: "700",
                  color: "#333333",
                }}
              >
                P
              </Typography>
            )} */}

            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: "700",
                color: "#333333",
              }}
            >
              {editor.isActive("heading", { level: 1 }) && "H1"}
              {editor.isActive("heading", { level: 2 }) && "H2"}
              {editor.isActive("heading", { level: 3 }) && "H3"}
              {editor.isActive("heading", { level: 4 }) && "H4"}
              {editor.isActive("heading", { level: 5 }) && "H5"}
              {editor.isActive("heading", { level: 6 }) && "H6"}
            </Typography>
            {openHeadingsDialog && <ChevronUp />}
            {!openHeadingsDialog && <ChevronDown />}
          </Button>

          {openHeadingsDialog && (
            <Box
              sx={{
                position: "absolute",
                backgroundColor: "white",
                borderRadius: "4px",
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
                zIndex: 1,
                // padding: "0.1rem 0.5rem",
                width: "150px",
              }}
            >
              {/* add support for paragraph */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  padding: "0.1rem 0.2rem",
                  fontSize: "16px",
                  fontWeight: "500",
                  color: "#333333",
                  cursor: "pointer",
                }}
              >
                <Button
                  onClick={() => {
                    editor.chain().focus().setParagraph().run();
                    setIsExplicitParagraph(true);
                    setOpenHeadingsDialog(false);
                  }}
                  sx={{
                    color: "#333333",
                  }}
                >
                  P &nbsp; Paragraph
                </Button>
              </Box>

              {[1, 2, 3, 4, 5, 6].map((level) => (
                <Box
                  sx={{
                    display: "flex",
                    // justifyContent: "space-between",
                    alignItems: "flex-start",
                    padding: "0.1rem 0.2rem",

                    flexDirection: "column",
                    // padding: "0.1rem 0.2rem",
                    fontSize: "16px",
                    fontWeight: "500",
                    color: "#333333",

                    cursor: "pointer",
                  }}
                  key={`heading-option-${level}`}
                >
                  <Button
                    key={`heading-option-${level}`}
                    onClick={() => {
                      editor
                        .chain()
                        .focus()
                        .toggleHeading({ level: level as Level })
                        .run();
                      setOpenHeadingsDialog(false);
                    }}
                    sx={{
                      color: "#333333",
                      fontSize: "14px",
                    }}
                  >
                    H
                    <span style={{ fontSize: "12px", marginTop: "4px" }}>
                      {level}
                    </span>
                    &nbsp;&nbsp;Heading {level}
                  </Button>
                </Box>
              ))}
            </Box>
          )}
        </Box>
        {/* /divider*/}
        <Box
          sx={{
            backgroundColor: "white",
          }}
        >
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              border: "1px solid #E0E0E0",
              borderColor: "gray !important",

              height: "30px !important",
              margin: "0px 4px !important",
            }}
          />
        </Box>
        <Button
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Bold"
          sx={{
            color: editor.isActive("bold") ? "blue" : "#333333",
          }}
        >
          <FormatBoldIcon />
        </Button>
        <Button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Italic"
          sx={{
            color: editor.isActive("italic") ? "blue" : "#333333",
          }}
        >
          <FormatItalicIcon />
        </Button>
        <Button
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title="Underline"
          sx={{
            color: editor.isActive("underline") ? "blue" : "#333333",
          }}
        >
          <FormatUnderlinedIcon />
        </Button>
        <Button
          onClick={() => editor.chain().focus().unsetAllMarks().run()}
          title="Clear Formatting"
        >
          <FormatClearIcon
            sx={{
              color: "#333333",
            }}
          />
        </Button>
        {/* text color */}
        <Box>
          <Button
            onClick={() => {
              setOpenTextColorDialog(!openTextColorDialog);
              setSelectedColor("#000000");
            }}
            sx={{
              position: "relative",
              color: "#333333",
            }}
            title="Text Color"
          >
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: "700",
                textDecoration: "underline",
                textDecorationColor: selectedColor,
                textDecorationThickness: "4px",
                textUnderlineOffset: "3px",
                textDecorationStyle: "solid",
              }}
            >
              A
            </Typography>

            {openTextColorDialog ? <ChevronUp /> : <ChevronDown />}
          </Button>
          {openTextColorDialog && (
            <Box
              sx={{
                position: "absolute",
                backgroundColor: "white",
                borderRadius: "4px",
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
                zIndex: 1,
                width: "250px",
                padding: "0.5rem",
              }}
            >
              <CirclePicker
                color={selectedColor}
                onChangeComplete={(color) => {
                  setSelectedColor(color.hex);

                  editor.chain().focus().setColor(color.hex).run();
                }}
              />
            </Box>
          )}
        </Box>
        {/* /divider*/}
        <Box
          sx={{
            backgroundColor: "white",
          }}
        >
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              border: "1px solid #E0E0E0",
              borderColor: "gray !important",

              height: "30px !important",
              margin: "0px 4px !important",
            }}
          />
        </Box>

        {/* align */}
        <Box>
          <Button
            onClick={() => setOpenAlignDialog(!openAlignDialog)}
            sx={{
              position: "relative",
              color: openAlignDialog ? "blue" : "#333333",
            }}
          >
            <FormatAlignLeftIcon />
            {openAlignDialog && <ChevronUp />}
            {!openAlignDialog && <ChevronDown />}
          </Button>

          {openAlignDialog && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "absolute",
                backgroundColor: "white",
                borderRadius: "4px",
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
                zIndex: 1,
                width: "150px",
              }}
            >
              <Button
                onClick={() =>
                  editor.chain().focus().setTextAlign("left").run()
                }
                sx={{
                  position: "relative",
                  color: "#333333",
                }}
              >
                <FormatAlignLeftIcon />
              </Button>

              <Button
                onClick={() =>
                  editor.chain().focus().setTextAlign("center").run()
                }
                sx={{
                  color: "#333333",
                }}
                title="Align Center"
              >
                <FormatAlignCenterIcon />
              </Button>
              <Button
                onClick={() =>
                  editor.chain().focus().setTextAlign("right").run()
                }
                title="Align Right"
                sx={{
                  color: "#333333",
                }}
              >
                <FormatAlignRightIcon />
              </Button>
            </Box>
          )}
        </Box>

        {/* Lists */}
        <Button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Bullet List"
          sx={{
            color: editor.isActive("bulletList") ? "blue" : "#333333",
          }}
        >
          <FormatListBulletedIcon />
        </Button>
        <Button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Numbered List"
          sx={{
            color: editor.isActive("orderedList") ? "blue" : "#333333",
          }}
        >
          <FormatListNumberedIcon />
        </Button>
        {/* add link button  */}
        <Button
          onClick={() => setOpenLinkDialog(true)}
          title="Add Link"
          sx={{ color: editor.isActive("link") ? "blue" : "#333333" }}
        >
          <LinkIcon />
        </Button>
        {/* add image button */}
        <Button
          onClick={() => setOpenImageDialog(true)}
          title="Insert Image"
          sx={{ color: "#333333" }}
        >
          <ImageIcon />
        </Button>

        {/* /divider*/}
        <Box
          sx={{
            backgroundColor: "white",
          }}
        >
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              border: "1px solid #E0E0E0",
              borderColor: "gray !important",

              height: "30px !important",
              margin: "0px 4px !important",
            }}
          />
        </Box>
        {/* code */}

        <Button
          onClick={() => editor.chain().focus().toggleCode().run()}
          title="Code"
          sx={{
            color: editor.isActive("code") ? "blue" : "#333333",
          }}
        >
          <CodeIcon />
        </Button>
        {/* quote */}
        <Button
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          title="Quote"
          sx={{
            color: editor.isActive("blockquote") ? "blue" : "#333333",
          }}
        >
          <FormatQuoteIcon />
        </Button>

        {/* Table button with dimension selector */}
        <Button
          onClick={handleTableButtonClick}
          title="Insert Table"
          sx={{
            color: editor.isActive("table") ? "blue" : "#333333",
          }}
        >
          <TableChartIcon />
        </Button>

        <Popover
          open={Boolean(tableAnchorEl)}
          anchorEl={tableAnchorEl}
          onClose={handleTablePopoverClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          <Box
            sx={{
              p: 1,
              backgroundColor: "white",
            }}
          >
            <Typography
              sx={{
                p: 1,
                fontSize: "14px",
                color: "#666",
              }}
            >
              {hoveredDimensions.rows > 0
                ? `${hoveredDimensions.rows} × ${hoveredDimensions.cols} Table`
                : "Insert Table"}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `repeat(${maxCols}, 20px)`,
                gap: "2px",
                p: 1,
              }}
            >
              {Array.from({ length: maxRows * maxCols }).map((_, index) => {
                const row = Math.floor(index / maxCols);
                const col = index % maxCols;
                return (
                  <Box
                    key={index}
                    onClick={handleTableInsert}
                    onMouseEnter={() => handleCellHover(row, col)}
                    sx={{
                      width: "20px",
                      height: "20px",
                      border: "1px solid #ddd",
                      backgroundColor:
                        row < hoveredDimensions.rows &&
                        col < hoveredDimensions.cols
                          ? "#e3f2fd"
                          : "white",
                      cursor: "pointer",
                      "&:hover": {
                        border: "1px solid #2196f3",
                      },
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        </Popover>

        {/* humanize button */}

        {/* ask ai button */}
        <Tooltip
          open={showSelectionTooltip}
          title="Please select text first"
          onClose={() => setShowSelectionTooltip(false)}
        >
          <Button
            variant="contained"
            sx={{
              width: "120px",
              height: "40px",
              padding: "8px 22px",
              fontSize: "16px",
              fontWeight: 400,
              mr: "10px",
              color: "#5048E5",
              border: "1px solid #5048E5 !important",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            disabled={isEEATMode}
            onClick={() => {
              const hasSelection = !editor.state.selection.empty;
              if (!hasSelection) {
                setShowSelectionTooltip(true);
                // Auto-hide tooltip after 2 seconds
                setTimeout(() => setShowSelectionTooltip(false), 2000);
              } else {
                //  call handleAidropdown in articleEditor
                handleAidropdown();
              }
            }}
          >
            <MagicWandIcon
              sx={{
                mt: 1,
                color: "#5048E5",
                "& path": { fill: "#5048E5" },
              }}
            />
            Ask AI
          </Button>
        </Tooltip>
      </ButtonGroup>
      <Dialog
        open={openLinkDialog}
        onClose={() => setOpenLinkDialog(false)}
        onKeyDown={(e) => {
          e.stopPropagation();
        }}
      >
        <DialogTitle>Add Link</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="URL"
            type="url"
            fullWidth
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onPaste={(e) => {
              e.stopPropagation();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLinkDialog(false)}>Cancel</Button>
          <Button
            onClick={() => {
              if (linkUrl) {
                editor
                  .chain()
                  .focus()
                  .extendMarkRange("link")
                  .setLink({ href: linkUrl })
                  .run();
              }
              setLinkUrl("");
              setOpenLinkDialog(false);
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openImageDialog}
        onClose={() => setOpenImageDialog(false)}
        onKeyDown={(e) => {
          e.stopPropagation();
        }}
      >
        <DialogTitle>Insert Image</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Image URL"
            type="url"
            fullWidth
            placeholder="https://example.com/image.jpg"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            onPaste={(e) => {
              e.stopPropagation();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenImageDialog(false)}>Cancel</Button>
          <Button
            onClick={() => {
              if (imageUrl) {
                editor.chain().focus().setImage({ src: imageUrl }).run();
              }
              setImageUrl("");
              setOpenImageDialog(false);
            }}
          >
            Insert
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MenuBar;
